<script>

/**
 * Remote Select
 * 
 * Permite la obentción con agrupación de datos dinámica desde servirdor para integración 
 * diracta o cointegración con ui-edit-auto-modal.
 * 
 * Ejemplos:
 * 
 * <RemoteSelect v-model="testMod" label="Usuarios con etiqueta" remote="ui/usuarios" allow-grouping data-group="grouping" allow-filter multiline=2 />
 * <RemoteSelect v-model="testMod" label="Bloqueado" remote="ui/usuarios" readonly />
 * <RemoteSelect v-model="testMod" allow-grouping data-group="inicial" :remote="dyn" label="Dynamic URI" allow-filter />
 * 
 * Muestra de endpoint: (MVCLite)
 * main_usuarios()
 * 
 * Devuelve una lista completa de usuarios si el tipo de usuario actual es admin, 
 * si es un restaruante, develve solo los datos de su cuewnta.
 * 
 * public function main_usuarios($mode = 'all')
 * {
 *     try {
 * 
 *         $nameStr = 'upper(concat(nombre, " ", apellidos ))';
 * 
 *         $config = [
 *             'values'    => "id_pk as value, concat($nameStr, ' - (', usuario, ')') as text, tipoUsuario as grouping, LEFT($nameStr, 1) AS inicial",
 *             'from'      => 'usuario',
 *             'order_by'  => "$nameStr ASC, trim(usuario) ASC",
 *             'where'     => 'cuentaBorrada = 0 AND estatus = 1'
 *         ];
 * 
 *         switch ($mode) {
 *             case 'admins':
 *                 $config['where'] = implode(' AND ', ['tipoUsuario = "Admin"', $config['where']]);
 *                 break;
 *             case 'rest':
 *                 $config['where'] = implode(' AND ', ['tipoUsuario = "Restaurant"', $config['where']]);
 *                 break;
 *             case 'clients':
 *                 $config['where'] = implode(' AND ', ['tipoUsuario = "Normal"', $config['where']]);
 *                 break;
 *         }
 * 
 *         $data = $this->{$this->models['main']}->getAllRows($config);
 *         $this->success(null, $data);
 *     } catch (Exception $e) {
 *         $this->fail($e->getMessage());
 *     }
 * }
 * 
 * @version 1.0
 * @author Daniel Lepe
 * @date 27 de Julio. 2023
 */
import mobiscroll from '@mobiscroll/javascript'
import axios from 'axios'
import _ from 'lodash'

export default {

    props: {
        remote: {
            type: String,
            required: true
        },
        value: {
            type: [String, Number]
        },
        label: {
            type: [String, Number]
        },
        readonly: {
            type: Boolean,
            default: false
        },
        allowFilter: {
            type: Boolean,
            default: false
        },
        dataValue: {
            type: String,
            default: 'value'
        },
        dataText: {
            type: String,
            default: 'text'
        },
        dataGroup: {
            type: String,
            default: 'grouping'
        },
        groupConfig: {
            type: Object,
            default: () => {
                return {
                    groupWheel: true,
                    header: true,
                    clustered: false
                }
            }
        },
        allowGrouping: {
            type: Boolean,
            default: false
        },
        inputClass: {
            type: String,
            default: 'form-control'
        },
        multiline: {
            type: [Number, String],
            default: 1
        },
        placeholder: {
            type: [String, Number, Boolean],
            default: 'Sin seleccionar...'
        }
    },

    computed: {

        requestingURI(){
            if(this.remote.startsWith('http')){
                return this.remote;
            } else {
                return axios.defaults.baseURL + (this.remote.startsWith('/')? '' : '/')  + this.remote;
            }
        }

    },

    data: () => {
        return {
            currentId: null,
            mobi: null,
            workingValue: null,
            fetchedOptions: []
        }
    },

    methods: {
        initialize() {
            this.mobi = mobiscroll.select('#' + this.currentId, {
                display: 'bubble',   
                disabled: this.readonly,
                filter: this.allowFilter,
                inputClass: this.inputClass,
                filterPlaceholderText: 'Escribe para filtrar',
                group: (this.allowGrouping) ? this.groupConfig : false,
                multiline: this.multiline,
                groupLabel: 'as',
                dataGroup: this.dataGroup,
                dataValue: this.dataValue,
                dataText: this.dataText,
                readOnly: this.readOnly,
                data: this.fetchedOptions,
                placeholder: this.placeholder,
                onSet: (event, inst) => {
                    this.emit(inst.getVal())
                }
            })
        },
        async loadData() {
            try {
                const response = await axios.get(this.remote)
                if(response.status){
                    this.fetchedOptions = response.data;
                }
            } catch(e){
                console.error(e)
            }
        },
        emit(val){
            let selected = {}
            if(val) {
                selected = this.getFullSelected()
            } else {
                selected[this.dataValue] = null;
                selected[this.dataText] = null;
            }
            this.$emit('input', val);
            this.$emit('changed', val, selected);
        },
        reviewReallyBanned(){
            this.mobi.option({
                disabled: this.readonly,
                inputClass: this.inputClass + ( (!this.readonly) ? " notReallyBanned" : "") 
            })
        },
        getFullSelected(){
            return _.cloneDeep(_.find(this.fetchedOptions, opt => {
                return (opt[this.dataValue] == this.workingValue )
            }))
        }
    },

    watch: {
        placeholder(){
            this.mobi.option({
                placeholder: this.placeholder
            })
        },
        readonly(){
            this.reviewReallyBanned()
        },
        value(){
            if(this.value != this.workingValue){
                this.mobi.setVal(this.value, true)
            }
        },
        async remote(){
            this.mobi.cancel();
            this.mobi.disable();
            await this.loadData()
            if(this.workingValue){
                let isInList = _.findIndex(this.fetchedOptions, opt => {
                    return (opt[this.dataValue] == this.workingValue)
                })
                if(isInList == -1){
                    this.mobi.setVal(null, true)
                    this.emit(null)
                }
            }
            this.mobi.refresh(this.fetchedOptions)
            this.mobi.enable();
        }
    },
    
    beforeMount(){
        this.currentId = _.uniqueId('remote-sselect')
        if(this.value)
            this.workingValue = _.clone(this.value)
    },

    async mounted(){
        await this.loadData()
        this.initialize()
        if(this.workingValue)
            this.mobi.setVal(this.workingValue, true)
        this.reviewReallyBanned()
    },

    beforeDestroy(){
        this.mobi.destroy(true)
    }

}

</script>

<template>
    <div class="remoteSelect">
        <b-form-group v-if="label" :label="label" :label-for="currentId">
            <b-form-select :id="currentId" v-model="workingValue" :placeholder="placeholder"/>
        </b-form-group>
        <b-form-input v-else :id="currentId" v-model="workingValue" :placeholder="placeholder"/>
    </div>
</template>

<style lang="less">
 .remoteSelect {
    margin: 0 !important;
    padding: 0 !important;
    .form-control.notReallyBanned:disabled, .form-control.notReallyBanned[readonly] {
        background-color: var(--white) !important;
    }
 }
</style>